import { apiFiles } from "@/api/file.api.ts";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { Box, Stack } from "@skailer-com/uikit";
import { Editor } from "@tiptap/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface CommentInputProps {
  comment?: string;
  files?: ApiFile[];
  date?: Date | null;
  onChangeComment: (comment: string) => void;
  onChangeFiles: (files: ApiFile[]) => void;
  onChangeDate?: (date: Date | null) => void;
  className?: string;
}

export const CommentInput = ({
  comment = "",
  files = [],
  date = null,
  onChangeComment,
  onChangeFiles,
  onChangeDate,
  className,
}: CommentInputProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState(comment);
  const [filesInner, setFilesInner] = useState<ApiFile[]>(files);
  const { handleErrors } = useErrorHandle();
  const editorRef = useRef<Editor | null>(null);

  async function removeFile(file: ApiFile) {
    setFilesInner(filesInner.filter((f) => f.id !== file.id));
    onChangeFiles(filesInner.filter((f) => f.id !== file.id));

    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  }

  return (
    <Stack gap={"sm"} className={className}>
      <RichEditor
        value={text}
        hideToolbar={true}
        label={t("common:add_comment")}
        highlightMentions={true}
        likeTextArea={true}
        ref={editorRef}
        onChange={(value) => {
          setText(value);
          onChangeComment(value);
        }}
      />
      {filesInner.length > 0 && (
        <Stack gap={"sm"}>
          {filesInner.map((f) => (
            <FileItem
              key={`file-${f.id}`}
              file={f}
              onRemove={() => {
                removeFile(f);
              }}
            />
          ))}
        </Stack>
      )}
      <Stack items={"center"} direction={"horizontal"} gap={"sm"}>
        <Box>
          <FileUpload
            buttonText={t("common:attach")}
            entityId={undefined}
            entityType={ApiEntityTypes.employee}
            afterSend={(file) => {
              if (file) {
                setFilesInner([...filesInner, file]);
                onChangeFiles([...filesInner, file]);
              }
            }}
          />
        </Box>
        {onChangeDate && (
          <Box>
            <Datepicker
              interfaceTypes="button"
              value={date}
              onChange={(newDate) => onChangeDate(newDate)}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
